import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Star, ShoppingCart, X, Loader2 } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const imgUrl = (src) => (src?.startsWith("/api") ? `${process.env.REACT_APP_BACKEND_URL}${src}` : src);

export default function Products() {
  const [products, setProducts] = useState([]);
  const [selected, setSelected] = useState(null);
  const [buyer, setBuyer] = useState({ name: "", phone: "", address: "" });
  const [sending, setSending] = useState(false);

  const priceValue = (p) => parseInt((p || "").replace(/\D/g, ""), 10) || 0;
  const fmt = (n) => n.toLocaleString("sr-RS") + " RSD";

  useEffect(() => {
    axios.get(`${API}/products`).then((r) => setProducts(r.data)).catch(() => {});
  }, []);

  const submitOrder = async (e) => {
    e.preventDefault();
    if (!buyer.name.trim() || !buyer.phone.trim() || !buyer.address.trim()) {
      toast.error("Popunite ime, telefon i adresu dostave");
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/orders`, {
        product_id: selected.id,
        product_name: selected.name,
        price: selected.price,
        customer_name: buyer.name,
        customer_phone: buyer.phone,
        customer_address: buyer.address,
      });
      toast.success("Porudžbina poslata!", { description: `Pozvaćemo vas radi potvrde za: ${selected.name}` });
      setSelected(null);
      setBuyer({ name: "", phone: "", address: "" });
    } catch {
      toast.error("Došlo je do greške", { description: "Pokušajte ponovo ili nas pozovite direktno." });
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="proizvodi" data-testid="products-section" className="relative py-24 lg:py-32 px-6 lg:px-12">
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7, ease: "easeOut" }}
          className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14"
        >
          <div>
            <span className="text-red-500 text-xs font-bold tracking-[0.3em] uppercase">Prodavnica</span>
            <h2 className="font-display uppercase text-4xl sm:text-5xl lg:text-6xl text-white mt-3 leading-none">
              Naši <span className="text-red-500">Proizvodi</span>
            </h2>
          </div>
          <p className="text-zinc-500 max-w-sm text-sm leading-relaxed">
            Provereni delovi i auto kozmetika vrhunskog kvaliteta — uz mogućnost ugradnje u našem servisu.
          </p>
        </motion.div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {products.map((p, i) => (
            <motion.div
              key={p.id}
              data-testid={`product-card-${p.id}`}
              initial={{ opacity: 0, y: 36 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.6, delay: (i % 4) * 0.08, ease: "easeOut" }}
              whileHover={{ y: -6 }}
              className="group bg-[#121216] border border-white/[0.07] rounded-2xl overflow-hidden hover:border-red-600/40 hover:shadow-[0_8px_40px_rgba(227,22,30,0.15)] transition-colors"
            >
              <div className="relative aspect-[4/3] overflow-hidden bg-[#0c0c0f]">
                {p.image && (
                  <img src={imgUrl(p.image)} alt={p.name}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-[rgba(9,9,11,0.6)] to-transparent" />
                <span className="absolute top-3 left-3 bg-red-600 text-white text-xs font-bold px-3 py-1.5 rounded-full">
                  {p.price}
                </span>
                {p.rating && (
                  <span className="absolute top-3 right-3 flex items-center gap-1 bg-[rgba(9,9,11,0.75)] backdrop-blur-sm text-white text-xs font-semibold px-2.5 py-1.5 rounded-full">
                    <Star size={11} className="text-amber-400 fill-amber-400" /> {p.rating}
                  </span>
                )}
              </div>
              <div className="p-5">
                <span className="text-[10px] font-bold tracking-[0.2em] uppercase text-red-400/80">{p.category}</span>
                <h3 className="text-white font-semibold mt-1.5 leading-snug">{p.name}</h3>
                <button
                  data-testid={`product-order-${p.id}`}
                  onClick={() => setSelected(p)}
                  className="mt-4 w-full flex items-center justify-center gap-2 border border-white/15 hover:border-red-500/60 hover:bg-red-600 text-white text-sm font-semibold py-2.5 rounded-full transition-all active:scale-[0.97]"
                >
                  <ShoppingCart size={15} /> Poruči Odmah
                </button>
              </div>
            </motion.div>
          ))}
        </div>
      </div>

      <AnimatePresence>
        {selected && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] flex items-center justify-center p-5 bg-black/70 backdrop-blur-sm"
            onClick={() => setSelected(null)}
          >
            <motion.form
              data-testid="order-modal"
              onSubmit={submitOrder}
              onClick={(e) => e.stopPropagation()}
              initial={{ opacity: 0, scale: 0.94, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.94, y: 20 }}
              transition={{ duration: 0.25 }}
              className="bg-[#121216] border border-white/10 rounded-3xl p-7 w-full max-w-md"
            >
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h3 className="font-display text-2xl text-white">Poruči Proizvod</h3>
                  <p className="text-sm text-zinc-400 mt-1.5">{selected.name} · <span className="text-red-400 font-semibold">{selected.price}</span>
                    <span className="text-zinc-500"> + Dostava</span>
                  </p>
                </div>
                <button type="button" data-testid="order-modal-close" onClick={() => setSelected(null)}
                  className="p-2 text-zinc-500 hover:text-white transition-colors">
                  <X size={18} />
                </button>
              </div>
              <div className="mt-6 space-y-4">
                <input data-testid="order-name-input" type="text" placeholder="Ime i prezime *" value={buyer.name}
                  onChange={(e) => setBuyer({ ...buyer, name: e.target.value })} required
                  className="w-full bg-[#0c0c0f] border border-white/10 focus:border-red-500/70 rounded-xl px-4 py-3.5 text-sm text-white placeholder:text-zinc-600 outline-none transition-colors" />
                <input data-testid="order-phone-input" type="tel" placeholder="Broj telefona *" value={buyer.phone}
                  onChange={(e) => setBuyer({ ...buyer, phone: e.target.value })} required
                  className="w-full bg-[#0c0c0f] border border-white/10 focus:border-red-500/70 rounded-xl px-4 py-3.5 text-sm text-white placeholder:text-zinc-600 outline-none transition-colors" />
                <input data-testid="order-address-input" type="text" placeholder="Adresa dostave (ulica, grad) *" value={buyer.address}
                  onChange={(e) => setBuyer({ ...buyer, address: e.target.value })} required
                  className="w-full bg-[#0c0c0f] border border-white/10 focus:border-red-500/70 rounded-xl px-4 py-3.5 text-sm text-white placeholder:text-zinc-600 outline-none transition-colors" />
              </div>
              <div data-testid="order-price-summary" className="mt-5 bg-[#0c0c0f] border border-white/10 rounded-xl p-4 space-y-2 text-sm">
                <div className="flex justify-between text-zinc-400">
                  <span>Proizvod</span><span className="text-white">{selected.price}</span>
                </div>
                <div className="flex justify-between text-zinc-400">
                  <span>Dostava</span>
                  <span className="text-white font-semibold">BexExpress</span>
                </div>
                <div className="flex justify-between pt-2 border-t border-white/10 font-bold">
                  <span className="text-zinc-300">Ukupno</span>
                  <span data-testid="order-total-price" className="text-red-500">{fmt(priceValue(selected.price))}</span>
                </div>
                <p className="text-[11px] text-zinc-600">Dostavu vrši BexExpress kurirska služba — plaćanje pouzećem.</p>
              </div>
              <button data-testid="order-submit-button" type="submit" disabled={sending}
                className="mt-6 w-full flex items-center justify-center gap-2 bg-red-600 hover:bg-red-500 disabled:opacity-60 text-white font-bold py-3.5 rounded-full transition-all active:scale-[0.98]">
                {sending ? <Loader2 size={17} className="animate-spin" /> : <ShoppingCart size={16} />}
                {sending ? "Slanje..." : "Potvrdi Porudžbinu"}
              </button>
              <p className="text-center text-xs text-zinc-600 mt-4">Plaćanje pouzećem — pozvaćemo vas radi potvrde.</p>
            </motion.form>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
